import { useState } from 'react';
import usePutRequest from '../../Hooks/usePutRequest';
import {
    TextField, Button, MenuItem, Typography, Box, InputLabel,
    Select, FormControl, OutlinedInput, Chip, Stack, Avatar,
    Paper, Alert, CircularProgress
} from '@mui/material';
import {
    PhotoCamera, Business, Link, Category
} from '@mui/icons-material';
import { useAuth } from '../../Context/AuthContext';
import { brandGradient } from '../../shared-theme/themePrimitives';

const roles = ['Owner', 'Marketing Manager', 'Content Creator'];
const industries = ['Fashion', 'Tech', 'Food', 'Education'];
const categories = ['Music', 'Fashion', 'Gaming', 'Healthcare', 'Education', 'Travel', 'Comedy', 'Tech'];
const platforms = ['Instagram', 'YouTube', 'TikTok', 'Twitter/X', 'Facebook', 'Reddit'];
const budgets = ['<$1,000', '$1,000-$10,000', '$10,000-$50,000', '>$50,000'];

export default function BrandProfileForm({ defaultValues, isEditing = false, onSave }) {
    const { user } = useAuth();
    const { put, loading } = usePutRequest();

    const [formData, setFormData] = useState({
        brandName: defaultValues?.brand_name || defaultValues?.name || '',
        role: defaultValues?.brand?.role_in_brand || '',
        industry: defaultValues?.brand?.industry || '',
        category: defaultValues?.brand?.category || [],
        platforms: defaultValues?.brand?.target_platforms || [],
        budget: defaultValues?.brand?.annual_budget || '',
        description: defaultValues?.brand.description || '',
        socialLinks: defaultValues?.brand.profile_links?.join(', ') || '',
        profileImageDisplay: defaultValues?.brand?.profile_image
            ? `${process.env.REACT_APP_BASE_URL}${defaultValues?.brand?.profile_image}`
            : '',
        coverImageDisplay: defaultValues?.brand?.cover_image
            ? `${process.env.REACT_APP_BASE_URL}${defaultValues?.brand?.cover_image}`
            : '',
        profileImageValue: defaultValues?.brand?.profile_image || '',
        coverImageValue: defaultValues?.brand?.cover_image || '',
        profileImageChanged: false,
        coverImageChanged: false
    });

    const [submitError, setSubmitError] = useState('');

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setSubmitError('Image size should be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            if (field === 'profileImage') {
                setFormData(prev => ({
                    ...prev,
                    profileImageDisplay: reader.result,
                    profileImageValue: reader.result,
                    profileImageChanged: true
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    coverImageDisplay: reader.result,
                    coverImageValue: reader.result,
                    coverImageChanged: true
                }));
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!formData.brandName.trim()) {
            setSubmitError('Brand name is required');
            return;
        }

        const payload = {
            name: user.name,
            email: user.email,
            brand_name: formData.brandName,
            role_in_brand: formData.role,
            industry: formData.industry,
            annual_budget: formData.budget,
            category: formData.category,
            description: formData.description,
            target_platforms: formData.platforms,
            profile_links: formData.socialLinks
                .split(',')
                .map(link => link.trim())
                .filter(link => link),
            website: "https://xeriotech.com",
            profile_image: formData.profileImageValue,
            cover_image: formData.coverImageValue
        };

        try {
            const response = await put('/update-profile', payload);

            if (response === 200) {
                await onSave();
            } else {
                setSubmitError("Failed to update profile. Please try again.");
            }
        } catch (error) {
            console.error("Update failed", error);
            setSubmitError(error.message || "An error occurred while updating the profile.");
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, px: 2 }}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h4" sx={{
                    fontWeight: 700,
                    background: brandGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                    textAlign: "center", mb: 3, width: '100%'
                }}>
                    {isEditing ? 'Edit Brand Profile' : 'Create Brand Profile'}
                </Typography>

                {submitError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {submitError}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Brand Identity */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Business sx={{ mr: 1 }} /> Brand Identity
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Brand Name"
                                fullWidth
                                required
                                value={formData.brandName}
                                onChange={(e) => handleChange('brandName', e.target.value)}
                                error={!formData.brandName.trim() && submitError}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Role in Brand</InputLabel>
                                <Select
                                    value={formData.role}
                                    onChange={(e) => handleChange('role', e.target.value)}
                                    label="Role in Brand"
                                >
                                    {roles.map((role) => (
                                        <MenuItem key={role} value={role}>{role}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Paper>

                    {/* Brand Details */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Category sx={{ mr: 1 }} /> Brand Details
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Industry</InputLabel>
                                <Select
                                    value={formData.industry}
                                    onChange={(e) => handleChange('industry', e.target.value)}
                                    label="Industry"
                                >
                                    {industries.map(ind => (
                                        <MenuItem key={ind} value={ind}>{ind}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Annual Budget</InputLabel>
                                <Select
                                    value={formData.budget}
                                    onChange={(e) => handleChange('budget', e.target.value)}
                                    label="Annual Budget"
                                >
                                    {budgets.map(b => (
                                        <MenuItem key={b} value={b}>{b}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    multiple
                                    value={formData.category}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                    input={<OutlinedInput label="Category" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map(value => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {categories.map(cat => (
                                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Brand Description"
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                            />
                        </Stack>
                    </Paper>

                    {/* Digital Presence */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <Link sx={{ mr: 1 }} /> Digital Presence
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Target Platforms</InputLabel>
                                <Select
                                    multiple
                                    value={formData.platforms}
                                    onChange={(e) => handleChange('platforms', e.target.value)}
                                    input={<OutlinedInput label="Target Platforms" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map(value => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {platforms.map(p => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Social Media Links (comma separated)"
                                fullWidth
                                value={formData.socialLinks}
                                onChange={(e) => handleChange('socialLinks', e.target.value)}
                                placeholder="https://instagram.com/brand, https://facebook.com/brand"
                            />
                        </Stack>
                    </Paper>

                    {/* Media Section */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2 }}>
                            <PhotoCamera sx={{ mr: 1 }} /> Brand Media
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                            {/* Profile Image Upload */}
                            <Box sx={{ flex: 1 }}>
                                <Button variant="outlined" component="label" fullWidth>
                                    Upload Profile Image
                                    <input
                                        hidden
                                        accept="image/*"
                                        type="file"
                                        onChange={(e) => handleImageUpload(e, 'profileImage')}
                                    />
                                </Button>
                                {formData.profileImageDisplay && (
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Avatar
                                            src={formData.profileImageDisplay}
                                            sx={{ width: 80, height: 80 }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            {/* Cover Image Upload */}
                            <Box sx={{ flex: 1 }}>
                                <Button variant="outlined" component="label" fullWidth>
                                    Upload Cover Image
                                    <input
                                        hidden
                                        accept="image/*"
                                        type="file"
                                        onChange={(e) => handleImageUpload(e, 'coverImage')}
                                    />
                                </Button>
                                {formData.coverImageDisplay && (
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            src={formData.coverImageDisplay}
                                            alt="Cover"
                                            style={{
                                                width: '100%',
                                                maxHeight: 120,
                                                objectFit: 'cover',
                                                borderRadius: 8
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </Paper>

                    <Box sx={{ textAlign: 'right' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                backgroundImage: brandGradient,
                                color: '#fff',
                                borderRadius: 50,
                                px: 4,
                                minWidth: 120,
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEditing ? 'Update Profile' : 'Save Profile'
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
}